import React from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
	MODAL_TYPE,
} from '../constants/snippetAnalytics.ts';
import { useOpenSnippetPanel } from '../hooks/panel/useOpenSnippetPanel.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';

import { FirstSnippetCreatedModal } from './onboarding/FirstSnippetCreatedModal.tsx';
import { getSnippetBaseModalProps, SnippetBaseModal } from './base/SnippetBaseModal.tsx';
import { messages as baseMessages } from './base/SnippetBaseModal.i18n.ts';
import { isBodyTooLargeError } from '../services/types/blockServiceTypes.ts';
import { wrapAdfWithDocNode } from '../utils/adfUtils.ts';
import type { SnippetBaseModalFormValues } from './utils/snippetModalFormUtils.ts';
import { getDefaultFormValues } from './utils/snippetModalFormUtils.ts';
import { useSnippetModalClose } from '../hooks/modals/useSnippetModalClose.ts';
import { createSnippetModalMessages } from './CreateSnippetModal.i18n.ts';

export type CreateSnippetModalProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	adf?: ADFEntity | ADFEntity[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & WithFlagsProps;

const FORM_KEY = 'create-snippet';

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	onClose,
	onCloseComplete,
	adf,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
}) => {
	const intl = useIntl();
	const openSnippetPanel = useOpenSnippetPanel();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { showModal } = useDialogs();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
	);
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const handleClose = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET,
	);

	const {
		data: onboardingData,
		loading: onboardingLoading,
		error: onboardingError,
	} = useGetOnboardingState([SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL], false);
	const { hasSeenFirstSnippetCreatedModal } = deserializeState(onboardingData);

	const defaultValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const initialAdf = wrapAdfWithDocNode(adf);
	const title = intl.formatMessage(createSnippetModalMessages.titleCreate);
	const submitLabel = intl.formatMessage(createSnippetModalMessages.createButton);

	const successFlagActions = [
		{
			content: intl.formatMessage(baseMessages.successFlagViewTemplateAction),
			onClick: openSnippetPanel,
		},
	];

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const bodySize = JSON.stringify(currentAdf).length;
		const isOnboardingStateReady = !onboardingLoading && !onboardingError;
		const shouldShowFirstSnippetCreatedModal =
			!isEmbeddedConfluence && isOnboardingStateReady && !hasSeenFirstSnippetCreatedModal;

		const newSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
				successFlagActions,
				showSuccessFlag: !shouldShowFirstSnippetCreatedModal,
				onShareFailure: (snippetId) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
				onSaveFailure: (error) => {
					const isBodyTooLarge = isBodyTooLargeError(error);
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_CREATE, {
						...(isBodyTooLarge && { isBodyTooLarge: true, bodySize }),
					});
				},
				messages: {
					successFlagTitle: createSnippetModalMessages.successFlagTitle,
					errorFlagTitle: createSnippetModalMessages.errorFlagTitle,
					errorFlagDescription: createSnippetModalMessages.errorFlagDescription,
				},
			},
		);
		if (newSnippet) {
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SAVE_SUCCESS, {
				snippetId: newSnippet.snippetId,
				bodySize,
				modalType: MODAL_TYPE.CREATE,
			});
			handleClose({
				outcome: MODAL_OUTCOME.CREATED,
				snippetId: newSnippet.snippetId,
			});

			if (shouldShowFirstSnippetCreatedModal) {
				showModal(FirstSnippetCreatedModal, {});
			}
		}
	};

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey: FORM_KEY,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				isSnippetSharingForSiteEnabled,
			})}
		/>
	);
};

export const CreateSnippetModal = withFlags(CreateSnippetModalWithFlags);
